import { useEffect, useId, useRef, useState, type ClipboardEvent, type FormEvent } from "react";
import { Icon } from "../components/Icon";
import { TurnstileWidget, useCaptcha } from "../components/Turnstile";
import { LOGIN_HERO_IMAGE } from "../config/public";
import { DataError } from "../data";
import { DEMO_ACCOUNTS, DEMO_PASSWORD, DEMO_SIGNIN_CODE } from "../data/demo-provider";
import { LANGS, useI18n, type MessageKey } from "../i18n";
import { useSession } from "../state/session";

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
export const CODE_LENGTH = 6;
export const RESEND_COOLDOWN_S = 60;
/** Layout breakpoint (also in login.css): below it the hero becomes a short banner. */
export const COMPACT_QUERY = "(max-width: 899px)";
const SITE_URL = "https://expartisemakine.com";
const SITE_LABEL = "expartisemakine.com";

type Method = "password" | "code";
type Busy = "password" | "send" | "verify" | "resend" | "demo" | null;

/** Login-specific wording for provider errors (ADR 0020). */
export function loginErrorKey(err: unknown): MessageKey {
  const code = err instanceof DataError ? err.code : null;
  switch (code) {
    case "invalid_credentials":
      return "login.error.invalid";
    case "captcha_failed":
      return "login.error.captcha";
    case "invalid_code":
      return "login.error.code";
    case "rate_limited":
      return "login.error.rateLimited";
    case "network":
      return "error.network";
    case "invalid_email":
      return "error.invalid_email";
    case "not_configured":
      return "login.error.notConfigured";
    default:
      return "login.error.generic";
  }
}

function useMediaQuery(query: string): boolean {
  const get = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(query).matches;
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(query);
    const on = () => setMatches(mql.matches);
    on();
    mql.addEventListener?.("change", on);
    return () => mql.removeEventListener?.("change", on);
  }, [query]);
  return matches;
}

/** Seconds left until `until` (epoch ms), re-rendering every second while > 0. */
function useCountdown(until: number | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (until === null) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [until]);
  return until === null ? 0 : Math.max(0, Math.ceil((until - now) / 1000));
}

const digitsOnly = (s: string) => s.replace(/\D/g, "").slice(0, CODE_LENGTH);

export function Login() {
  const { t, lang, setLang } = useI18n();
  const { signIn, signInWithCode, signInDemo, provider } = useSession();
  const isDemo = provider.mode === "demo";
  const compact = useMediaQuery(COMPACT_QUERY);
  const captcha = useCaptcha();
  const uid = useId();

  const [method, setMethod] = useState<Method>("password");
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(true);
  const [forgotHint, setForgotHint] = useState(false);
  const [codeSentTo, setCodeSentTo] = useState<string | null>(null);
  /** Remember choice at the time the code was sent: decides whether the e-mail link is mentioned. */
  const [sentRemember, setSentRemember] = useState(true);
  /** Resend wait per address (lower-case e-mail -> epoch ms), also across "use a different e-mail". */
  const [cooldowns, setCooldowns] = useState<Record<string, number>>({});
  const resendUntil = codeSentTo ? (cooldowns[codeSentTo.toLowerCase()] ?? null) : null;
  const [error, setError] = useState<MessageKey | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const [heroFailed, setHeroFailed] = useState(false);
  const cooldown = useCountdown(resendUntil);
  const codeInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step === "code") codeInput.current?.focus();
  }, [step]);

  const chooseMethod = (m: Method) => {
    setMethod(m);
    setError(null);
    if (m === "password") setForgotHint(false);
  };

  const onForgot = () => {
    setMethod("code");
    setStep("email");
    setForgotHint(true);
    setError(null);
  };

  const finishAttempt = () => {
    // Turnstile tokens are single-use; get a fresh one for the next attempt.
    if (captcha.enabled) captcha.reset();
  };

  const submitPassword = async () => {
    if (!email.trim() || !password) {
      setError("login.error.required");
      return;
    }
    setBusy("password");
    try {
      await signIn(email.trim(), password, { remember, captchaToken: captcha.token ?? undefined });
    } catch (err) {
      setError(loginErrorKey(err));
      setBusy(null);
      finishAttempt();
    }
  };

  const sendCode = async (kind: "send" | "resend") => {
    const value = (kind === "resend" && codeSentTo ? codeSentTo : email).trim();
    if (!EMAIL_PATTERN.test(value)) {
      setError("login.error.emailRequired");
      return;
    }
    const key = value.toLowerCase();
    const showSent = (startCooldown: boolean) => {
      if (startCooldown) setCooldowns((c) => ({ ...c, [key]: Date.now() + RESEND_COOLDOWN_S * 1000 }));
      setCodeSentTo(value);
      setSentRemember(remember);
      setStep("code");
      setCode("");
    };
    // Same address within the wait: show the code step without asking the server again, so the
    // screen behaves the same whether or not the address has an account.
    if ((cooldowns[key] ?? 0) > Date.now()) {
      showSent(false);
      return;
    }
    setBusy(kind);
    try {
      await provider.sendSignInCode(value, { remember, captchaToken: captcha.token ?? undefined });
      showSent(true);
    } catch (err) {
      // Rate limited = a code was sent recently; treat it like a send and keep the countdown.
      if (err instanceof DataError && err.code === "rate_limited") showSent(true);
      else setError(loginErrorKey(err));
    }
    setBusy(null);
    finishAttempt();
  };

  const verifyCode = async () => {
    if (code.length !== CODE_LENGTH || !codeSentTo) {
      setError("login.error.codeRequired");
      return;
    }
    setBusy("verify");
    try {
      await signInWithCode(codeSentTo, code, { remember });
    } catch (err) {
      setError(loginErrorKey(err));
      setBusy(null);
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (method === "password") void submitPassword();
    else if (step === "email") void sendCode("send");
    else void verifyCode();
  };

  const onCodePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = digitsOnly(e.clipboardData.getData("text"));
    if (!pasted) return;
    e.preventDefault();
    setCode(pasted);
  };

  const onDemo = async () => {
    setError(null);
    setBusy("demo");
    try {
      await signInDemo();
    } catch (err) {
      setError(loginErrorKey(err));
      setBusy(null);
    }
  };

  const needsCaptcha = method === "password" || step === "email";
  const submitDisabled = busy !== null || (needsCaptcha && !captcha.ready);
  const submitLabel =
    method === "password"
      ? busy === "password"
        ? t("login.submitting")
        : t("login.submit")
      : step === "email"
        ? busy === "send"
          ? t("login.code.sending")
          : t("login.code.send")
        : busy === "verify"
          ? t("login.submitting")
          : t("login.submit");
  const errorId = `${uid}-error`;
  const describedBy = error ? errorId : undefined;

  const methods: Array<{ id: Method; title: MessageKey; desc: MessageKey; icon: "lock" | "mail" }> = [
    { id: "password", title: "login.method.password", desc: "login.method.passwordDesc", icon: "lock" },
    { id: "code", title: "login.method.code", desc: "login.method.codeDesc", icon: "mail" },
  ];

  return (
    <div className={`signin${compact ? " is-compact" : ""}`}>
      <div className="signin-panel">
        <header className="signin-head">
          <img src="/brand/expartise-logo.png" alt={t("app.logoAlt")} className="signin-logo" width={150} height={48} />
          <div className="signin-lang" role="group" aria-label={t("topbar.language")}>
            {LANGS.map((l) => (
              <button
                key={l}
                type="button"
                lang={l}
                aria-pressed={lang === l}
                aria-label={l === "tr" ? t("lang.trName") : t("lang.enName")}
                className={lang === l ? "is-on" : undefined}
                onClick={() => setLang(l)}
              >
                {l === "tr" ? t("lang.tr") : t("lang.en")}
              </button>
            ))}
          </div>
        </header>

        <main className="signin-main" aria-labelledby="login-title">
          <p className="signin-eyebrow">{t("login.eyebrow")}</p>
          <h1 id="login-title" className="signin-title">
            {t("login.title")}
          </h1>
          <p className="signin-subtitle">{t("login.subtitle")}</p>
          {isDemo && <span className="demo-chip demo-chip-inline">{t("app.demoBadge")}</span>}

          <form onSubmit={onSubmit} noValidate className="signin-form">
            <fieldset className="signin-methods">
              <legend className="signin-label">{t("login.method.legend")}</legend>
              <div className="signin-method-grid">
                {methods.map((m) => (
                  <label key={m.id} className={`signin-method${method === m.id ? " is-on" : ""}`}>
                    <input
                      type="radio"
                      name={`${uid}-method`}
                      value={m.id}
                      checked={method === m.id}
                      onChange={() => chooseMethod(m.id)}
                      className="signin-method-input"
                    />
                    <span className="signin-method-icon" aria-hidden="true">
                      <Icon name={m.icon} size={18} />
                    </span>
                    <span className="signin-method-text">
                      <span className="signin-method-title">{t(m.title)}</span>
                      <span className="signin-method-desc">{t(m.desc)}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            {method === "code" && forgotHint && (
              <p className="signin-hint" role="note">
                <Icon name="info" size={16} />
                <span>{t("login.forgot.hint")}</span>
              </p>
            )}

            {(method === "password" || step === "email") && (
              <div className="field">
                <label htmlFor="login-email">{t("login.email")}</label>
                <input
                  id="login-email"
                  type="email"
                  inputMode="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-invalid={error ? true : undefined}
                  aria-describedby={describedBy}
                />
              </div>
            )}

            {method === "password" && (
              <div className="field">
                <label htmlFor="login-password">{t("login.password")}</label>
                <div className="signin-password">
                  <input
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={describedBy}
                  />
                  <button
                    type="button"
                    className="signin-eye"
                    aria-pressed={showPassword}
                    aria-controls="login-password"
                    aria-label={showPassword ? t("login.password.hide") : t("login.password.show")}
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    <Icon name={showPassword ? "eyeOff" : "eye"} size={18} />
                  </button>
                </div>
              </div>
            )}

            {method === "code" && step === "code" && codeSentTo && (
              <>
                <div className="signin-sent" role="status">
                  <Icon name="mail" size={18} />
                  <div>
                    <p>{t(sentRemember ? "login.code.sent" : "login.code.sentCodeOnly", { email: codeSentTo })}</p>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => {
                        setStep("email");
                        setError(null);
                      }}
                    >
                      {t("login.code.changeEmail")}
                    </button>
                  </div>
                </div>
                <div className="field">
                  <label htmlFor="login-code">{t("login.code.label")}</label>
                  <input
                    ref={codeInput}
                    id="login-code"
                    className="signin-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]*"
                    maxLength={CODE_LENGTH}
                    value={code}
                    onChange={(e) => setCode(digitsOnly(e.target.value))}
                    onPaste={onCodePaste}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={describedBy ? `${describedBy} login-code-help` : "login-code-help"}
                  />
                  <span id="login-code-help" className="signin-field-help">
                    {t("login.code.help", { n: CODE_LENGTH })}
                  </span>
                </div>
              </>
            )}

            <div className="signin-row">
              <label className="signin-check">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
                <span>{t("login.remember")}</span>
              </label>
              {method === "password" && (
                <button type="button" className="link-button signin-forgot" onClick={onForgot}>
                  {t("login.forgot")}
                </button>
              )}
              {method === "code" && step === "code" && (
                <button
                  type="button"
                  className="link-button signin-forgot"
                  disabled={busy !== null || cooldown > 0 || !captcha.ready}
                  onClick={() => {
                    setError(null);
                    void sendCode("resend");
                  }}
                >
                  {cooldown > 0 ? t("login.code.resendIn", { s: cooldown }) : busy === "resend" ? t("login.code.sending") : t("login.code.resend")}
                </button>
              )}
            </div>

            <TurnstileWidget captcha={captcha} action="login" />

            {error && (
              <p id={errorId} className="form-error" role="alert">
                {t(error, { n: CODE_LENGTH })}
              </p>
            )}

            <button type="submit" className="button button-primary button-block signin-submit" disabled={submitDisabled}>
              {submitLabel}
            </button>
            {needsCaptcha && captcha.enabled && !captcha.ready && busy === null && <p className="signin-field-help">{t("login.captcha.waiting")}</p>}
            <p className="signin-help">{t("login.help")}</p>
          </form>

          {isDemo && (
            <details className="signin-demo">
              <summary>{t("login.demo.summary")}</summary>
              <div className="signin-demo-body">
                <p className="signin-field-help">{t("login.demoNote")}</p>
                <button type="button" className="button button-secondary button-block" onClick={() => void onDemo()} disabled={busy !== null}>
                  {busy === "demo" ? t("login.submitting") : t("login.demo")}
                </button>
                <p className="signin-field-help">{t("login.demoAccounts", { password: DEMO_PASSWORD })}</p>
                <ul className="signin-demo-accounts">
                  {DEMO_ACCOUNTS.map((a) => (
                    <li key={a.email}>
                      <button
                        type="button"
                        className="link-button mono"
                        onClick={() => {
                          chooseMethod("password");
                          setEmail(a.email);
                          setPassword(DEMO_PASSWORD);
                        }}
                      >
                        {a.email}
                      </button>
                      <span className="demo-role">{t(`role.${a.role}`)}</span>
                    </li>
                  ))}
                </ul>
                <p className="signin-field-help">{t("login.demoCode", { code: DEMO_SIGNIN_CODE })}</p>
              </div>
            </details>
          )}
        </main>

        <footer className="signin-footer">
          <span>{t("login.footerCompany")}</span>
          <span aria-hidden="true"> · </span>
          <a href={SITE_URL} target="_blank" rel="noopener noreferrer">
            {SITE_LABEL}
          </a>
        </footer>
      </div>

      <aside className={`signin-hero${heroFailed ? " is-fallback" : ""}`} aria-labelledby="login-hero-title">
        {!heroFailed && (
          <img
            className="signin-hero-img"
            src={LOGIN_HERO_IMAGE}
            alt=""
            loading="eager"
            decoding="async"
            referrerPolicy="no-referrer"
            onError={() => setHeroFailed(true)}
          />
        )}
        <div className="signin-hero-shade" aria-hidden="true" />
        <div className="signin-hero-text">
          <p className="signin-hero-eyebrow">{t("login.hero.eyebrow")}</p>
          <h2 id="login-hero-title" className="signin-hero-title">
            <span>{t("login.hero.line1")}</span> <span>{t("login.hero.line2")}</span>
          </h2>
          {!compact && <p className="signin-hero-caption">{t("login.hero.caption")}</p>}
        </div>
      </aside>
    </div>
  );
}
